import React, { useState, useEffect, useMemo } from 'react';
import { Job, Department } from '../types/models';
import { JobService } from '../services/jobService';
import { JobForm } from '../components/forms/JobForm';
import { DepartmentService } from '../services/departmentService';
import { useCompany } from '../contexts/CompanyContext';

export const JobsPage: React.FC = () => {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingJob, setEditingJob] = useState<Job | null>(null);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<number | null>(null);
  const { selectedCompany } = useCompany();

  useEffect(() => {
    loadJobs();
  }, []);

  // Загружаем отделы и сбрасываем фильтр при смене контрагента
  useEffect(() => {
    const loadDepartments = async () => {
      try {
        const response = await DepartmentService.getAll();
        if (response.success) {
          setDepartments(response.data);
        }
      } catch (_) {
        // no-op
      }
    };

    setSelectedDepartmentId(null);
    loadDepartments();
  }, [selectedCompany]);

  const loadJobs = async () => {
    try {
      setLoading(true);
      const response = await JobService.getAll();
      if (response.success) {
        setJobs(response.data);
      } else {
        setError(response.message || 'Ошибка загрузки должностей');
      }
    } catch (err) {
      setError('Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingJob(null);
    setShowForm(true);
  };

  const handleEdit = (job: Job) => {
    setEditingJob(job);
    setShowForm(true);
  };

  const handleDelete = async (job: Job) => {
    if (window.confirm(`Вы уверены, что хотите удалить должность "${job.name}"?`)) {
      try {
        const response = await JobService.delete(job.id);
        if (response.success) {
          loadJobs(); // Перезагрузить список
        } else {
          setError(response.message || 'Ошибка удаления');
        }
      } catch (err) {
        setError('Ошибка удаления должности');
      }
    }
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingJob(null);
    loadJobs(); // Перезагрузить список
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingJob(null);
  };

  const filteredJobs = useMemo(() => {
    if (!selectedDepartmentId) return jobs;
    return jobs.filter(j => (j.employees || []).some(e => e.departmentId === selectedDepartmentId));
  }, [jobs, selectedDepartmentId]);

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-error">
        {error}
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Должности</h1>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <select
            className="form-input"
            style={{ width: '240px' }}
            value={selectedDepartmentId || ''}
            onChange={(e) => setSelectedDepartmentId(Number(e.target.value) || null)}
          >
            <option value="">Все структурные подразделения</option>
            {departments.map((d) => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>
          <button className="btn btn-primary" onClick={handleCreate}>
            Добавить должность
          </button>
        </div>
      </div>

      <div className="card">
        <table className="table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Название должности</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {filteredJobs.map((job) => (
              <tr key={job.id}>
                <td>{job.id}</td>
                <td>{job.name}</td>
                <td>
                  <button
                    className="btn btn-secondary"
                    style={{ marginRight: '5px' }}
                    onClick={() => handleEdit(job)}
                  >
                    Редактировать
                  </button>
                  <button
                    className="btn btn-danger"
                    onClick={() => handleDelete(job)}
                  >
                    Удалить
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <JobForm
          job={editingJob || undefined}
          onSuccess={handleFormSuccess}
          onCancel={handleFormCancel}
        />
      )}
    </div>
  );
};
